// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson
// Procedural, colour-blind-safe critter art. Each type has a UNIQUE silhouette so
// shape alone identifies it; colour is a second, redundant cue. Every colour is
// held >=3:1 against the cream card surface by tests/contrast.test.ts.
import type { CritterType } from './game';

/** Critter fill colours — all dark-enough to clear 3:1 on the cream card. */
export const CRITTER_COLOR: Record<CritterType, string> = {
  spider: '#7a34ad',
  roach: '#8f5410',
  fly: '#0d6f7c',
  grub: '#2c7a2f',
  rat: '#ab2f52',
  bat: '#39508f',
};

/** Surfaces the renderer paints on — kept here so the contrast test can pin them. */
export const SURFACE = {
  felt: '#20161f',
  feltEdge: '#160f16',
  card: '#f4ebe0',
  cardEdge: '#d9cbb8',
  panel: '#2c1f2b',
  ink: '#f6efe6',
  inkCard: '#241a22',
} as const;

/** Non-critter colours that still carry meaning on the play surface. */
export const MEANING = {
  good: '#46c07a',
  bad: '#e5634e',
  accent: '#e0a94a',
  violet: '#b07be0',
  peek: '#6bb6d6',
} as const;

function chan(c: number): number {
  const v = c / 255;
  return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
}
export function luminance(hex: string): number {
  const h = hex.replace('#', '');
  const n = parseInt(h.length === 3 ? h.replace(/./g, '$&$&') : h, 16);
  return 0.2126 * chan((n >> 16) & 255) + 0.7152 * chan((n >> 8) & 255) + 0.0722 * chan(n & 255);
}
export function contrast(a: string, b: string): number {
  const la = luminance(a);
  const lb = luminance(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

export const CRITTER_NAME: Record<CritterType, string> = {
  spider: 'Spider',
  roach: 'Roach',
  fly: 'Fly',
  grub: 'Grub',
  rat: 'Rat',
  bat: 'Bat',
};

/**
 * Return an inline SVG for a critter. `mono` draws it in a single ink colour (for
 * the light-on-dark HUD chips); otherwise it uses the type colour with darker eyes.
 */
export function critterSvg(type: CritterType, opts: { size?: number; color?: string } = {}): string {
  const size = opts.size ?? 64;
  const c = opts.color ?? CRITTER_COLOR[type];
  const eye = '#20161f';
  const body = BODIES[type](c, eye);
  return `<svg class="critter critter-${type}" viewBox="0 0 100 100" width="${size}" height="${size}" aria-hidden="true" focusable="false">${body}</svg>`;
}

type Draw = (c: string, eye: string) => string;

const eyes = (cx1: number, cx2: number, cy: number, r: number, eye: string): string =>
  `<circle cx="${cx1}" cy="${cy}" r="${r}" fill="${eye}"/><circle cx="${cx2}" cy="${cy}" r="${r}" fill="${eye}"/>`;

const BODIES: Record<CritterType, Draw> = {
  spider: (c, eye) => `
    <g stroke="${c}" stroke-width="6" stroke-linecap="round" fill="none">
      <path d="M50 55 L20 34 M50 55 L14 50 M50 55 L18 68 M50 55 L26 84"/>
      <path d="M50 55 L80 34 M50 55 L86 50 M50 55 L82 68 M50 55 L74 84"/>
    </g>
    <ellipse cx="50" cy="58" rx="20" ry="24" fill="${c}"/>
    <circle cx="50" cy="40" r="13" fill="${c}"/>
    ${eyes(45, 55, 38, 3.2, eye)}`,
  roach: (c, eye) => `
    <g stroke="${c}" stroke-width="5" stroke-linecap="round" fill="none">
      <path d="M35 62 L16 74 M35 66 L18 82 M65 62 L84 74 M65 66 L82 82 M40 40 L30 24 M60 40 L70 24"/>
    </g>
    <ellipse cx="50" cy="58" rx="24" ry="30" fill="${c}"/>
    <path d="M50 30 v56" stroke="${eye}" stroke-width="3" opacity="0.35"/>
    <ellipse cx="50" cy="36" rx="15" ry="12" fill="${c}"/>
    ${eyes(44, 56, 34, 3, eye)}`,
  fly: (c, eye) => `
    <ellipse cx="30" cy="42" rx="20" ry="12" fill="${c}" opacity="0.55" transform="rotate(-24 30 42)"/>
    <ellipse cx="70" cy="42" rx="20" ry="12" fill="${c}" opacity="0.55" transform="rotate(24 70 42)"/>
    <ellipse cx="50" cy="60" rx="14" ry="24" fill="${c}"/>
    <circle cx="50" cy="40" r="13" fill="${c}"/>
    ${eyes(44, 56, 38, 4, eye)}`,
  grub: (c, eye) => `
    <g fill="${c}">
      <circle cx="30" cy="66" r="13"/>
      <circle cx="44" cy="60" r="14"/>
      <circle cx="59" cy="54" r="14"/>
      <circle cx="72" cy="46" r="13"/>
    </g>
    <path d="M72 40 l7 -8 M72 44 l9 -3" stroke="${c}" stroke-width="4" stroke-linecap="round"/>
    ${eyes(69, 77, 44, 2.6, eye)}`,
  rat: (c, eye) => `
    <path d="M62 62 q28 6 30 -14 q-14 6 -14 -2" fill="none" stroke="${c}" stroke-width="5" stroke-linecap="round"/>
    <ellipse cx="46" cy="60" rx="26" ry="20" fill="${c}"/>
    <circle cx="26" cy="46" r="12" fill="${c}"/>
    <circle cx="18" cy="34" r="8" fill="${c}"/>
    <circle cx="34" cy="30" r="8" fill="${c}"/>
    <circle cx="22" cy="48" r="2.6" fill="${eye}"/>
    <circle cx="14" cy="52" r="2.4" fill="${eye}"/>`,
  bat: (c, eye) => `
    <path d="M50 54 C30 30 18 34 8 30 C20 40 14 52 22 56 C30 50 40 52 50 60 Z" fill="${c}"/>
    <path d="M50 54 C70 30 82 34 92 30 C80 40 86 52 78 56 C70 50 60 52 50 60 Z" fill="${c}"/>
    <ellipse cx="50" cy="56" rx="15" ry="18" fill="${c}"/>
    <path d="M40 40 l-4 -12 l10 8 Z M60 40 l4 -12 l-10 8 Z" fill="${c}"/>
    ${eyes(45, 55, 52, 3, eye)}`,
};

/** The face-down card back — a felt-toned pattern, no critter, so nothing leaks. */
export function cardBackSvg(size = 64): string {
  return `<svg class="cardback" viewBox="0 0 100 140" width="${size}" height="${Math.round(
    (size * 140) / 100,
  )}" aria-hidden="true"><rect x="3" y="3" width="94" height="134" rx="12" fill="${SURFACE.panel}" stroke="${SURFACE.feltEdge}" stroke-width="3"/><g stroke="#5a3f56" stroke-width="3" opacity="0.6"><path d="M20 30 h60 M20 55 h60 M20 80 h60 M20 105 h60"/></g><circle cx="50" cy="70" r="16" fill="none" stroke="#7a5875" stroke-width="4"/></svg>`;
}
